import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// Validate REACT_APP_API_URL at startup — throws immediately if not configured.
// Set it in .env (development) or .env.production (production build).
import './config/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/common.css';
import './styles/App.css';
import './styles/theme-dashboard.css';
import './styles/ThemeToggle.css';
import './i18n/i18n';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { AppAccessProvider } from './context/AppAccessContext';
import AppRoute from './components/AppRoute';
import { ModuleProvider } from './context/ModuleContext';
import OfflineBanner from './components/OfflineBanner';
import LoginPage from './pages/LoginPage';
import WaiterLoginPage from './pages/Waiter/WaiterLoginPage';
import AppHomePage from './pages/AppHome/AppHomePage';
import RegisterPage from './pages/RegisterPage';
import ActivatePage from './pages/ActivatePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AttributeListPage from './pages/Products/AttributeListPage';
import AttributeCreatePage from './pages/Products/AttributeCreatePage';
import AttributeEditPage from './pages/Products/AttributeEditPage';
import BarcodeSettingsPage from './pages/Products/BarcodeSettingsPage';
import DashboardPage from './pages/DashboardPage';
import ProductListPage from './pages/Products/ProductListPage';
import ProductCreatePage from './pages/Products/ProductCreatePage';
import ProductEditPage from './pages/Products/ProductEditPage';
import ProductDetailPage from './pages/Products/ProductDetailPage';
import ProductCategoryListPage from './pages/Products/ProductCategoryListPage';
import ProductCategoryCreatePage from './pages/Products/ProductCategoryCreatePage';
import ProductCategoryEditPage from './pages/Products/ProductCategoryEditPage';
import ProductCategoryDetailPage from './pages/Products/ProductCategoryDetailPage';
import ProductCategoryDeletePage from './pages/Products/ProductCategoryDeletePage';
import ProductDeletePage from './pages/Products/ProductDeletePage';
import PurchaseItemListPage from './pages/PurchaseItems/PurchaseItemListPage';
import PurchaseItemCreatePage from './pages/PurchaseItems/PurchaseItemCreatePage';
import PurchaseItemDetailPage from './pages/PurchaseItems/PurchaseItemDetailPage';
import PurchaseItemEditPage from './pages/PurchaseItems/PurchaseItemEditPage';
import PurchaseItemDeletePage from './pages/PurchaseItems/PurchaseItemDeletePage';
import SalesItemListPage from './pages/SalesItems/SalesItemListPage';
import SalesItemCreatePage from './pages/SalesItems/SalesItemCreatePage';
import SalesItemDetailPage from './pages/SalesItems/SalesItemDetailPage';
import SalesItemEditPage from './pages/SalesItems/SalesItemEditPage';
import SalesItemDeletePage from './pages/SalesItems/SalesItemDeletePage';
import SalesItemBarcodePrintPage from './pages/SalesItems/SalesItemBarcodePrintPage';
import StockLevelsPage from './pages/Stock/StockLevelsPage';
import StockAdjustmentPage from './pages/Stock/StockAdjustmentPage';
import StockMovementPage from './pages/Stock/StockMovementPage';
import StockReportPage from './pages/Stock/StockReportPage';
import PurchaseOrderListPage from './pages/PurchaseOrders/PurchaseOrderListPage';
import PurchaseOrderCreatePage from './pages/PurchaseOrders/PurchaseOrderCreatePage';
import PurchaseOrderDetailPage from './pages/PurchaseOrders/PurchaseOrderDetailPage';
import PurchaseOrderEditPage from './pages/PurchaseOrders/PurchaseOrderEditPage';
import PurchaseOrderDeletePage from './pages/PurchaseOrders/PurchaseOrderDeletePage';
import PurchaseOrderApprovalsPage from './pages/PurchaseOrders/PurchaseOrderApprovalsPage';
import PurchaseOrderCancellationApprovalsPage from './pages/PurchaseOrders/PurchaseOrderCancellationApprovalsPage';
import SupplierOutstandingPaymentsPage from './pages/PurchaseOrders/SupplierOutstandingPaymentsPage';
import SalesDashboardPage from './modules/sales/pages/Sales/SalesDashboardPage';
import SalesInvoiceListPage from './modules/sales/pages/SalesInvoice/SalesInvoiceListPage';
import SalesInvoiceDetailPage from './modules/sales/pages/SalesInvoice/SalesInvoiceDetailPage';
import SalesInvoiceCreatePage from './modules/sales/pages/SalesInvoice/SalesInvoiceCreatePage';
import SalesInvoiceInquiryPage from './modules/sales/pages/SalesInvoice/SalesInvoiceInquiryPage';
import CustomerOutstandingReviewPage from './modules/sales/pages/SalesInvoice/CustomerOutstandingReviewPage';
import InstallmentPlanPage from './modules/sales/pages/SalesInvoice/InstallmentPlanPage';
import ServiceItemsPage from './modules/sales/pages/ServiceItems/ServiceItemsPage';
import WarehouseListPage from './pages/Warehouses/WarehouseListPage';
import SupplierListPage from './pages/Suppliers/SupplierListPage';
import SupplierCreatePage from './pages/Suppliers/SupplierCreatePage';
import SupplierEditPage from './pages/Suppliers/SupplierEditPage';
import SupplierDetailPage from './pages/Suppliers/SupplierDetailPage';
import SupplierDeletePage from './pages/Suppliers/SupplierDeletePage';
import CustomerListPage from './modules/sales/pages/Customers/CustomerListPage';
import CustomerCreatePage from './modules/sales/pages/Customers/CustomerCreatePage';
import CustomerEditPage from './modules/sales/pages/Customers/CustomerEditPage';
import CustomerDetailPage from './modules/sales/pages/Customers/CustomerDetailPage';
import CustomerDeletePage from './modules/sales/pages/Customers/CustomerDeletePage';
import DepartmentListPage from './pages/Departments/DepartmentListPage';
import DepartmentCreatePage from './pages/Departments/DepartmentCreatePage';
import DepartmentDetailPage from './pages/Departments/DepartmentDetailPage';
import DepartmentEditPage from './pages/Departments/DepartmentEditPage';
import DepartmentDeletePage from './pages/Departments/DepartmentDeletePage';
import TaxListPage from './pages/Products/TaxListPage';
import TaxCreatePage from './pages/Products/TaxCreatePage';
import TaxDetailPage from './pages/Products/TaxDetailPage';
import TaxEditPage from './pages/Products/TaxEditPage';
import TaxDeletePage from './pages/Products/TaxDeletePage';
import CouponListPage from './pages/Coupons/CouponListPage';
import CouponCreatePage from './pages/Coupons/CouponCreatePage';
import CouponEditPage from './pages/Coupons/CouponEditPage';
import PromotionListPage from './pages/Promotions/PromotionListPage';
import PromotionCreatePage from './pages/Promotions/PromotionCreatePage';
import PromotionEditPage from './pages/Promotions/PromotionEditPage';
import ProductBundleListPage from './pages/ProductBundles/ProductBundleListPage';
import ProductBundleCreatePage from './pages/ProductBundles/ProductBundleCreatePage';
import ProductBundleEditPage from './pages/ProductBundles/ProductBundleEditPage';
import EmployeeListPage from './pages/HR/EmployeeListPage';
import EmployeeCreatePage from './pages/HR/EmployeeCreatePage';
import EmployeeEditPage from './pages/HR/EmployeeEditPage';
import AttendancePage from './pages/HR/AttendancePage';
import LeaveRequestListPage from './pages/HR/LeaveRequestListPage';
import LeaveRequestCreatePage from './pages/HR/LeaveRequestCreatePage';
import UnitListPage from './pages/Products/UnitListPage';
import UnitCreatePage from './pages/Products/UnitCreatePage';
import UnitDetailPage from './pages/Products/UnitDetailPage';
import UnitEditPage from './pages/Products/UnitEditPage';
import UnitDeletePage from './pages/Products/UnitDeletePage';
import UnitConversionListPage from './pages/Products/UnitConversionListPage';
import UnitConversionCreatePage from './pages/UnitConversions/UnitConversionCreatePage';
import UnitConversionDetailPage from './pages/UnitConversions/UnitConversionDetailPage';
import UnitConversionEditPage from './pages/UnitConversions/UnitConversionEditPage';
import UnitConversionDeletePage from './pages/UnitConversions/UnitConversionDeletePage';
import WarehouseCreatePage from './pages/Warehouses/WarehouseCreatePage';
import WarehouseDetailPage from './pages/Warehouses/WarehouseDetailPage';
import WarehouseEditPage from './pages/Warehouses/WarehouseEditPage';
import WarehouseDeletePage from './pages/Warehouses/WarehouseDeletePage';
import GRNListPage from './pages/GRN/GRNListPage';
import GRNCreatePage from './pages/GRN/GRNCreatePage';
import GRNDetailPage from './pages/GRN/GRNDetailPage';
import GRNEditPage from './pages/GRN/GRNEditPage';
import GRNApprovalsPage from './pages/GRN/GRNApprovalsPage';
import StockTransferListPage from './pages/StockTransfer/StockTransferListPage';
import StockTransferCreatePage from './pages/StockTransfer/StockTransferCreatePage';
import StockTransferDetailPage from './pages/StockTransfer/StockTransferDetailPage';
import StockTransferOutPage from './pages/StockTransfer/StockTransferOutPage';
import StockTransferApprovalPage from './pages/StockTransfer/StockTransferApprovalPage';
import StockTransferInPage from './pages/StockTransfer/StockTransferInPage';
import StockTransferHistoryPage from './pages/StockTransfer/StockTransferHistoryPage';
import StockReconciliationListPage from './pages/StockReconciliation/StockReconciliationListPage';
import StockReconciliationCreatePage from './pages/StockReconciliation/StockReconciliationCreatePage';
import StockReconciliationDetailPage from './pages/StockReconciliation/StockReconciliationDetailPage';
import DamageNoteListPage from './pages/DamageNote/DamageNoteListPage';
import DamageNoteCreatePage from './pages/DamageNote/DamageNoteCreatePage';
import DamageNoteDetailPage from './pages/DamageNote/DamageNoteDetailPage';
import DamageNoteApprovalsPage from './pages/DamageNote/DamageNoteApprovalsPage';
import DamageNoteHistoryPage from './pages/DamageNote/DamageNoteHistoryPage';
import IssueNoteListPage from './pages/IssueNote/IssueNoteListPage';
import IssueNoteCreatePage from './pages/IssueNote/IssueNoteCreatePage';
import IssueNoteDetailPage from './pages/IssueNote/IssueNoteDetailPage';
import IssueNoteApprovalsPage from './pages/IssueNote/IssueNoteApprovalsPage';
import IssueNoteHistoryPage from './pages/IssueNote/IssueNoteHistoryPage';
import UserListPage from './pages/Users/UserListPage';
import UserCreatePage from './pages/Users/UserCreatePage';
import UserDetailPage from './pages/Users/UserDetailPage';
import UserEditPage from './pages/Users/UserEditPage';
import UserDeactivatePage from './pages/Users/UserDeactivatePage';
import ReportsPage from './pages/Reports/ReportsPage';
import SalesReportsPage from './modules/sales/pages/Reports/SalesReportsPage';
import SalesSummaryReportPage from './modules/sales/pages/Reports/SalesSummaryReportPage';
import SalesByProductReportPage from './modules/sales/pages/Reports/SalesByProductReportPage';
import SalesByCustomerReportPage from './modules/sales/pages/Reports/SalesByCustomerReportPage';
import SalesTrendsReportPage from './modules/sales/pages/Reports/SalesTrendsReportPage';
import InventoryReportsPage from './pages/Reports/Inventory/InventoryReportsPage';
import StockSummaryReportPage from './pages/Reports/Inventory/StockSummaryReportPage';
import LowStockReportPage from './pages/Reports/Inventory/LowStockReportPage';
import DeadStockReportPage from './pages/Reports/Inventory/DeadStockReportPage';
import StockValuationReportPage from './pages/Reports/Inventory/StockValuationReportPage';
import StockMovementLedgerPage from './pages/Reports/Inventory/StockMovementLedgerPage';
import GRNReportPage from './pages/Reports/Inventory/GRNReportPage';
import StockAdjustmentReportPage from './pages/Reports/Inventory/StockAdjustmentReportPage';
import OpeningClosingStockReportPage from './pages/Reports/Inventory/OpeningClosingStockReportPage';
import RoleListPage from './pages/Roles/RoleListPage';
import RoleCreatePage from './pages/Roles/RoleCreatePage';
import RoleDetailPage from './pages/Roles/RoleDetailPage';
import RoleEditPage from './pages/Roles/RoleEditPage';
import RoleDeletePage from './pages/Roles/RoleDeletePage';
import CompanySettingsPage from './pages/Settings/CompanySettingsPage';
import DataImportPage from './pages/Settings/DataImportPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import BillPrintSettingsPage from './pages/Settings/BillPrintSettingsPage';
import PrintDevicesPage from './pages/Settings/PrintDevicesPage';
import ApprovalSettingsPage from './pages/Settings/ApprovalSettingsPage';
import ApprovalRuleCreatePage from './pages/Settings/ApprovalRuleCreatePage';
import ApprovalRuleEditPage from './pages/Settings/ApprovalRuleEditPage';
import UserProfilePage from './pages/Settings/UserProfilePage';
import PaymentTypeListPage from './modules/sales/pages/PaymentTypes/PaymentTypeListPage';
import PaymentTypeCreatePage from './modules/sales/pages/PaymentTypes/PaymentTypeCreatePage';
import PaymentTypeDetailPage from './modules/sales/pages/PaymentTypes/PaymentTypeDetailPage';
import PaymentTypeEditPage from './modules/sales/pages/PaymentTypes/PaymentTypeEditPage';
import PaymentTypeDeletePage from './modules/sales/pages/PaymentTypes/PaymentTypeDeletePage';
import PrivateRoute from './components/PrivateRoute';
import PermissionRoute from './components/PermissionRoute';
import TillGuardRoute from './components/TillGuardRoute';
import { TillProvider } from './context/TillContext';
import { CompanyProvider } from './context/CompanyContext';
import TillOpenPage from './modules/sales/pages/CashTill/TillOpenPage';
import TillClosePage from './modules/sales/pages/CashTill/TillClosePage';
import TillHistoryPage from './modules/sales/pages/CashTill/TillHistoryPage';
import DayEndPage from './modules/sales/pages/CashTill/DayEndPage';
import TillDetailPage from './modules/sales/pages/CashTill/TillDetailPage';
import { Permissions } from './constants/Permissions';
import MenuTypeListPage from './modules/restaurant/pages/MenuTypes/MenuTypeListPage';
import MenuTypeCreatePage from './modules/restaurant/pages/MenuTypes/MenuTypeCreatePage';
import MenuTypeDetailPage from './modules/restaurant/pages/MenuTypes/MenuTypeDetailPage';
import MenuCategoryListPage from './modules/restaurant/pages/MenuCategories/MenuCategoryListPage';
import MenuCategoryCreatePage from './modules/restaurant/pages/MenuCategories/MenuCategoryCreatePage';
import MenuCategoryDetailPage from './modules/restaurant/pages/MenuCategories/MenuCategoryDetailPage';
import MenuItemListPage from './modules/restaurant/pages/MenuItems/MenuItemListPage';
import MenuItemCreatePage from './modules/restaurant/pages/MenuItems/MenuItemCreatePage';
import MenuItemDetailPage from './modules/restaurant/pages/MenuItems/MenuItemDetailPage';
import OutletListPage from './pages/Outlets/OutletListPage';
import OutletCreatePage from './pages/Outlets/OutletCreatePage';
import OutletDetailPage from './pages/Outlets/OutletDetailPage';
import RestaurantOrderPage from './modules/restaurant/pages/Orders/RestaurantOrderPage';
import RestaurantOrdersListPage from './modules/restaurant/pages/Orders/RestaurantOrdersListPage';
import RestaurantOrderDetailPage from './modules/restaurant/pages/Orders/RestaurantOrderDetailPage';
import MenuModifierListPage from './modules/restaurant/pages/Modifiers/MenuModifierListPage';
import MenuModifierCreatePage from './modules/restaurant/pages/Modifiers/MenuModifierCreatePage';
import RecipeReferencePage from './modules/restaurant/pages/Recipes/RecipeReferencePage';
import MenuEngineeringReportPage from './modules/restaurant/pages/Reports/MenuEngineeringReportPage';
import HappyHourListPage from './modules/restaurant/pages/HappyHour/HappyHourListPage';
import HappyHourCreatePage from './modules/restaurant/pages/HappyHour/HappyHourCreatePage';
import KotVoidReasonsPage from './modules/restaurant/pages/KotVoidReasons/KotVoidReasonsPage';
import TableListPage from './modules/restaurant/pages/Tables/TableListPage';
import TableCreatePage from './modules/restaurant/pages/Tables/TableCreatePage';
import FloorPlanPage from './modules/restaurant/pages/Tables/FloorPlanPage';
import ReservationListPage from './modules/restaurant/pages/Reservations/ReservationListPage';
import ReservationCreatePage from './modules/restaurant/pages/Reservations/ReservationCreatePage';

import CashierOrderPage from './pages/Cashier/CashierOrderPage';
import WaiterNewOrderPage from './pages/Waiter/WaiterNewOrderPage';
import WaiterMyOrdersPage from './pages/Waiter/WaiterMyOrdersPage';
import KitchenDisplayPage from './pages/Kitchen/KitchenDisplayPage';

import AutoMobileVehicleListPage from './modules/automobile/pages/Vehicles/VehicleListPage';
import AutoMobileVehicleCreatePage from './modules/automobile/pages/Vehicles/VehicleCreatePage';
import AutoMobileVehicleDetailPage from './modules/automobile/pages/Vehicles/VehicleDetailPage';
import AutoMobileInvoiceListPage from './modules/automobile/pages/Invoices/InvoiceListPage';
import AutoMobileInvoiceCreatePage from './modules/automobile/pages/Invoices/InvoiceCreatePage';
import AutoMobileInvoiceDetailPage from './modules/automobile/pages/Invoices/InvoiceDetailPage';

import ChartOfAccountsPage from './modules/accounting/pages/ChartOfAccounts/ChartOfAccountsPage';
import AccountLedgerPage from './modules/accounting/pages/ChartOfAccounts/AccountLedgerPage';
import JournalEntriesListPage from './modules/accounting/pages/GeneralLedger/JournalEntriesListPage';
import JournalEntryDetailPage from './modules/accounting/pages/GeneralLedger/JournalEntryDetailPage';
import JournalEntryCreatePage from './modules/accounting/pages/GeneralLedger/JournalEntryCreatePage';
import TrialBalancePage from './modules/accounting/pages/GeneralLedger/TrialBalancePage';
import AccountsReceivablePage from './modules/accounting/pages/AccountsReceivable/AccountsReceivablePage';
import ARCustomerLedgerPage from './modules/accounting/pages/AccountsReceivable/ARCustomerLedgerPage';
import AccountsPayablePage from './modules/accounting/pages/AccountsPayable/AccountsPayablePage';
import APSupplierLedgerPage from './modules/accounting/pages/AccountsPayable/APSupplierLedgerPage';

import VehicleListPage from './modules/carRental/pages/Vehicles/VehicleListPage';
import VehicleCreatePage from './modules/carRental/pages/Vehicles/VehicleCreatePage';
import CarRentalBookingListPage from './modules/carRental/pages/Bookings/BookingListPage';
import CarRentalSchedulePage from './modules/carRental/pages/Schedule/SchedulePage';

function App() {
  return (
    <ThemeProvider>
      <OfflineBanner />
      <AuthProvider>
      <AppAccessProvider>
      <ModuleProvider>
      <CompanyProvider>
      <TillProvider>
      <Router>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/waiter-login" element={<WaiterLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<PrivateRoute />}>
          <Route path="/apps" element={<AppHomePage />} />
          <Route element={<PermissionRoute claim={Permissions.Products.View} />}>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>

          {/* ── Cashier POS (Cashier.Access permission required) ── */}
          {/* Bill History route intentionally omitted — reprinting past bills is a
              fraud vector, so cashiers have no route to browse or reprint history. */}
          <Route element={<PermissionRoute claim={Permissions.Cashier.Access} />}>
            <Route path="/cashier/order"        element={<CashierOrderPage />} />
          </Route>

          {/* ── Waiter PIN order-taking (Waiter.Access permission required) ── */}
          <Route element={<PermissionRoute claim={Permissions.Waiter.Access} />}>
            <Route path="/waiter/new-order" element={<WaiterNewOrderPage />} />
            <Route path="/waiter/my-orders" element={<WaiterMyOrdersPage />} />
          </Route>

          {/* ── Kitchen Display (Kitchen.Access permission required) ── */}
          <Route element={<PermissionRoute claim={Permissions.Kitchen.Access} />}>
            <Route path="/kitchen" element={<KitchenDisplayPage />} />
          </Route>

          {/* ── Products ── */}
          <Route element={<PermissionRoute claim={Permissions.Products.View} />}>
            <Route path="/products" element={<ProductListPage />} />
            <Route path="/products/categories" element={<ProductCategoryListPage />} />
            <Route path="/products/categories/:id" element={<ProductCategoryDetailPage />} />
            <Route path="/products/units" element={<UnitListPage />} />
            <Route path="/products/units/:id" element={<UnitDetailPage />} />
            <Route path="/products/unit-conversions" element={<UnitConversionListPage />} />
            <Route path="/products/unit-conversions/:id" element={<UnitConversionDetailPage />} />
            <Route path="/products/attributes" element={<AttributeListPage />} />
            <Route path="/purchase-items" element={<PurchaseItemListPage />} />
            <Route path="/purchase-items/:id" element={<PurchaseItemDetailPage />} />
            <Route path="/sales-items" element={<SalesItemListPage />} />
            <Route path="/sales-items/print-barcodes" element={<SalesItemBarcodePrintPage />} />
            <Route path="/sales-items/:id" element={<SalesItemDetailPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Products.Create} />}>
            <Route path="/products/unit-conversions/create" element={<UnitConversionCreatePage />} />
            <Route path="/products/categories/create" element={<ProductCategoryCreatePage />} />
            <Route path="/products/units/create" element={<UnitCreatePage />} />
            <Route path="/purchase-items/create" element={<PurchaseItemCreatePage />} />
            <Route path="/sales-items/create" element={<SalesItemCreatePage />} />
            <Route path="/products/create" element={<ProductCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Products.Edit} />}>
            <Route path="/products/attributes/create" element={<AttributeCreatePage />} />
            <Route path="/products/attributes/:id/edit" element={<AttributeEditPage />} />
            <Route path="/products/barcode-settings" element={<BarcodeSettingsPage />} />
            <Route path="/products/unit-conversions/:id/edit" element={<UnitConversionEditPage />} />
            <Route path="/products/categories/:id/edit" element={<ProductCategoryEditPage />} />
            <Route path="/products/units/:id/edit" element={<UnitEditPage />} />
            <Route path="/purchase-items/:id/edit" element={<PurchaseItemEditPage />} />
            <Route path="/sales-items/:id/edit" element={<SalesItemEditPage />} />
            <Route path="/products/:id/edit" element={<ProductEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Products.Delete} />}>
            <Route path="/products/unit-conversions/:id/delete" element={<UnitConversionDeletePage />} />
            <Route path="/products/categories/:id/delete" element={<ProductCategoryDeletePage />} />
            <Route path="/products/units/:id/delete" element={<UnitDeletePage />} />
            <Route path="/purchase-items/:id/delete" element={<PurchaseItemDeletePage />} />
            <Route path="/sales-items/:id/delete" element={<SalesItemDeletePage />} />
            <Route path="/products/:id/delete" element={<ProductDeletePage />} />
          </Route>

          {/* ── Taxes (own permission module) ── */}
          <Route element={<PermissionRoute claim={Permissions.Taxes.View} />}>
            <Route path="/products/taxes" element={<TaxListPage />} />
            <Route path="/products/taxes/:id" element={<TaxDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Taxes.Create} />}>
            <Route path="/products/taxes/create" element={<TaxCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Taxes.Edit} />}>
            <Route path="/products/taxes/:id/edit" element={<TaxEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Taxes.Delete} />}>
            <Route path="/products/taxes/:id/delete" element={<TaxDeletePage />} />
          </Route>

          {/* ── Coupons (own permission module) ── */}
          <Route element={<PermissionRoute claim={Permissions.Coupons.View} />}>
            <Route path="/sales/coupons" element={<CouponListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Coupons.Create} />}>
            <Route path="/sales/coupons/create" element={<CouponCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Coupons.Edit} />}>
            <Route path="/sales/coupons/:id/edit" element={<CouponEditPage />} />
          </Route>

          {/* ── Promotion rules (own permission module) ── */}
          <Route element={<PermissionRoute claim={Permissions.Promotions.View} />}>
            <Route path="/sales/promotions" element={<PromotionListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Promotions.Create} />}>
            <Route path="/sales/promotions/create" element={<PromotionCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Promotions.Edit} />}>
            <Route path="/sales/promotions/:id/edit" element={<PromotionEditPage />} />
          </Route>

          {/* ── Product bundles (own permission module) ── */}
          <Route element={<PermissionRoute claim={Permissions.ProductBundles.View} />}>
            <Route path="/sales/product-bundles" element={<ProductBundleListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.ProductBundles.Create} />}>
            <Route path="/sales/product-bundles/create" element={<ProductBundleCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.ProductBundles.Edit} />}>
            <Route path="/sales/product-bundles/:id/edit" element={<ProductBundleEditPage />} />
          </Route>

          {/* ── HR (standalone app — available regardless of the company's vertical) ── */}
          <Route element={<PermissionRoute claim={Permissions.Employees.View} />}>
            <Route path="/hr/employees" element={<EmployeeListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Employees.Create} />}>
            <Route path="/hr/employees/create" element={<EmployeeCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Employees.Edit} />}>
            <Route path="/hr/employees/:id/edit" element={<EmployeeEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AttendanceRecords.View} />}>
            <Route path="/hr/attendance" element={<AttendancePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.LeaveRequests.View} />}>
            <Route path="/hr/leave-requests" element={<LeaveRequestListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.LeaveRequests.Create} />}>
            <Route path="/hr/leave-requests/create" element={<LeaveRequestCreatePage />} />
          </Route>

          {/* ── Suppliers ── */}
          <Route element={<PermissionRoute claim={Permissions.Suppliers.View} />}>
            <Route path="/suppliers" element={<SupplierListPage />} />
            <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Suppliers.Create} />}>
            <Route path="/suppliers/create" element={<SupplierCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Suppliers.Edit} />}>
            <Route path="/suppliers/:id/edit" element={<SupplierEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Suppliers.Delete} />}>
            <Route path="/suppliers/:id/delete" element={<SupplierDeletePage />} />
          </Route>

          {/* ── Customers ── */}
          <Route element={<PermissionRoute claim={Permissions.Customers.View} />}>
            <Route path="/customers" element={<CustomerListPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Customers.Create} />}>
            <Route path="/customers/create" element={<CustomerCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Customers.Edit} />}>
            <Route path="/customers/:id/edit" element={<CustomerEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Customers.Delete} />}>
            <Route path="/customers/:id/delete" element={<CustomerDeletePage />} />
          </Route>

          {/* ── Warehouses ── */}
          <Route element={<PermissionRoute claim={Permissions.Warehouses.View} />}>
            <Route path="/organization/warehouses" element={<WarehouseListPage />} />
            <Route path="/organization/warehouses/:id" element={<WarehouseDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Warehouses.Create} />}>
            <Route path="/organization/warehouses/create" element={<WarehouseCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Warehouses.Edit} />}>
            <Route path="/organization/warehouses/:id/edit" element={<WarehouseEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Warehouses.Delete} />}>
            <Route path="/organization/warehouses/:id/delete" element={<WarehouseDeletePage />} />
          </Route>

          {/* ── Departments ── */}
          <Route element={<PermissionRoute claim={Permissions.Departments.View} />}>
            <Route path="/organization/departments" element={<DepartmentListPage />} />
            <Route path="/organization/departments/:id" element={<DepartmentDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Departments.Create} />}>
            <Route path="/organization/departments/create" element={<DepartmentCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Departments.Edit} />}>
            <Route path="/organization/departments/:id/edit" element={<DepartmentEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Departments.Delete} />}>
            <Route path="/organization/departments/:id/delete" element={<DepartmentDeletePage />} />
          </Route>

          {/* ── Stock ── */}
          <Route element={<PermissionRoute claim={Permissions.Stock.View} />}>
            <Route path="/stock" element={<StockLevelsPage />} />
            <Route path="/stock/levels" element={<StockLevelsPage />} />
            <Route path="/stock/movements" element={<StockMovementPage />} />
            <Route path="/stock/reports" element={<StockReportPage />} />
            <Route path="/stock-transfers" element={<StockTransferListPage />} />
            <Route path="/stock-transfers/:id" element={<StockTransferDetailPage />} />
            <Route path="/stock-reconciliations" element={<StockReconciliationListPage />} />
            <Route path="/stock-reconciliations/:id" element={<StockReconciliationDetailPage />} />
            {/* Stock Transfer focused views */}
            <Route path="/stock-transfer/out"      element={<StockTransferOutPage />} />
            <Route path="/stock-transfer/approval" element={<StockTransferApprovalPage />} />
            <Route path="/stock-transfer/in"       element={<StockTransferInPage />} />
            <Route path="/stock-transfer/history"  element={<StockTransferHistoryPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Stock.Adjust} />}>
            <Route path="/stock/adjust" element={<StockAdjustmentPage />} />
            <Route path="/stock-transfers/create" element={<StockTransferCreatePage />} />
            <Route path="/stock-transfers/:id/edit" element={<StockTransferCreatePage />} />
            <Route path="/stock-reconciliations/create" element={<StockReconciliationCreatePage />} />
            <Route path="/stock-reconciliations/:id/edit" element={<StockReconciliationCreatePage />} />
          </Route>

          {/* ── Damage Notes ── */}
          <Route element={<PermissionRoute claim={Permissions.DamageNotes.View} />}>
            <Route path="/damage-notes"            element={<DamageNoteListPage />} />
            <Route path="/damage-notes/approvals"  element={<DamageNoteApprovalsPage />} />
            <Route path="/damage-notes/history"    element={<DamageNoteHistoryPage />} />
            <Route path="/damage-notes/:id"        element={<DamageNoteDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.DamageNotes.Create} />}>
            <Route path="/damage-notes/create"     element={<DamageNoteCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.DamageNotes.Edit} />}>
            <Route path="/damage-notes/:id/edit"   element={<DamageNoteCreatePage />} />
          </Route>

          {/* ── Issue Notes ── */}
          <Route element={<PermissionRoute claim={Permissions.IssueNotes.View} />}>
            <Route path="/issue-notes"            element={<IssueNoteListPage />} />
            <Route path="/issue-notes/approvals"  element={<IssueNoteApprovalsPage />} />
            <Route path="/issue-notes/history"    element={<IssueNoteHistoryPage />} />
            <Route path="/issue-notes/:id"        element={<IssueNoteDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.IssueNotes.Create} />}>
            <Route path="/issue-notes/create"     element={<IssueNoteCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.IssueNotes.Edit} />}>
            <Route path="/issue-notes/:id/edit"   element={<IssueNoteCreatePage />} />
          </Route>

          {/* ── Purchase Orders ── */}
          <Route element={<PermissionRoute claim={Permissions.PurchaseOrders.View} />}>
            <Route path="/purchase/purchase-orders" element={<PurchaseOrderListPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrderListPage />} />
            <Route path="/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
            <Route path="/purchase-orders/outstanding" element={<SupplierOutstandingPaymentsPage />} />
            <Route path="/purchase-items" element={<PurchaseItemListPage />} />
            <Route path="/purchase-items/:id" element={<PurchaseItemDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PurchaseOrders.Create} />}>
            <Route path="/purchase-orders/create" element={<PurchaseOrderCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PurchaseOrders.Edit} />}>
            <Route path="/purchase-orders/:id/edit" element={<PurchaseOrderEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PurchaseOrders.Delete} />}>
            <Route path="/purchase-orders/:id/delete" element={<PurchaseOrderDeletePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PurchaseOrders.Approve} />}>
            <Route path="/purchase-orders/approvals" element={<PurchaseOrderApprovalsPage />} />
            <Route path="/purchase-orders/cancellation-approvals" element={<PurchaseOrderCancellationApprovalsPage />} />
          </Route>

          {/* ── Sales ── */}
          <Route element={<PermissionRoute claim={Permissions.SalesInvoices.View} />}>
            <Route path="/sales/dashboard" element={<SalesDashboardPage />} />
            <Route path="/sales/sales-invoices" element={<SalesInvoiceListPage />} />
            <Route path="/sales/sales-invoices/outstanding" element={<CustomerOutstandingReviewPage />} />
            <Route path="/sales/sales-invoices/inquiry" element={<SalesInvoiceInquiryPage />} />
            <Route path="/sales/sales-invoices/:id" element={<SalesInvoiceDetailPage />} />
            <Route path="/sales/sales-invoices/:id/installment-plan" element={<InstallmentPlanPage />} />
            <Route path="/sales-items" element={<SalesItemListPage />} />
            <Route path="/sales-items/:id" element={<SalesItemDetailPage />} />
            <Route path="/sales/service-items" element={<ServiceItemsPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.SalesInvoices.Create} />}>
            {/* Till guard — create & edit require an open till */}
            <Route element={<TillGuardRoute />}>
              <Route path="/sales/sales-invoices/create" element={<SalesInvoiceCreatePage />} />
            </Route>
          </Route>
          <Route element={<PermissionRoute claim={Permissions.SalesInvoices.Edit} />}>
            {/* Till guard — edit requires an open till */}
            <Route element={<TillGuardRoute />}>
              <Route path="/sales/sales-invoices/:id/edit" element={<SalesInvoiceCreatePage />} />
            </Route>
          </Route>

          {/* ── Cash Till ── */}
          <Route element={<PermissionRoute claim={Permissions.Till.View} />}>
            <Route path="/sales/till/history" element={<TillHistoryPage />} />
            <Route path="/sales/till/:id"     element={<TillDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Till.Open} />}>
            <Route path="/sales/till/open"  element={<TillOpenPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Till.Close} />}>
            <Route path="/sales/till/close" element={<TillClosePage />} />
            <Route path="/sales/day-end"    element={<DayEndPage />} />
          </Route>

          {/* ── GRN ── */}
          <Route element={<PermissionRoute claim={Permissions.GRN.View} />}>
            <Route path="/grn" element={<GRNListPage />} />
            <Route path="/grn/:id" element={<GRNDetailPage />} />
            <Route path="/grn/approvals" element={<GRNApprovalsPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.GRN.Create} />}>
            <Route path="/grn/create" element={<GRNCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.GRN.Edit} />}>
            <Route path="/grn/:id/edit" element={<GRNEditPage />} />
          </Route>

          {/* ── Users ── */}
          <Route element={<PermissionRoute claim={Permissions.Users.View} />}>
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/:id" element={<UserDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Users.Create} />}>
            <Route path="/users/create" element={<UserCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Users.Edit} />}>
            <Route path="/users/:id/edit" element={<UserEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Users.Delete} />}>
            <Route path="/users/:id/deactivate" element={<UserDeactivatePage />} />
          </Route>

          {/* ── Roles ── */}
          <Route element={<PermissionRoute claim={Permissions.Roles.View} />}>
            <Route path="/roles" element={<RoleListPage />} />
            <Route path="/roles/:id" element={<RoleDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Roles.Create} />}>
            <Route path="/roles/create" element={<RoleCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Roles.Edit} />}>
            <Route path="/roles/:id/edit" element={<RoleEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Roles.Delete} />}>
            <Route path="/roles/:id/delete" element={<RoleDeletePage />} />
          </Route>

          {/* ── Reports ── */}
          <Route element={<PermissionRoute claim={Permissions.Reports.View} />}>
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/sales" element={<SalesReportsPage />} />
            <Route path="/reports/sales/summary" element={<SalesSummaryReportPage />} />
            <Route path="/reports/sales/by-product" element={<SalesByProductReportPage />} />
            <Route path="/reports/sales/by-customer" element={<SalesByCustomerReportPage />} />
            <Route path="/reports/sales/trends" element={<SalesTrendsReportPage />} />
            <Route path="/reports/inventory" element={<InventoryReportsPage />} />
            <Route path="/reports/inventory/stock-summary" element={<StockSummaryReportPage />} />
            <Route path="/reports/inventory/low-stock" element={<LowStockReportPage />} />
            <Route path="/reports/inventory/dead-stock" element={<DeadStockReportPage />} />
            <Route path="/reports/inventory/valuation" element={<StockValuationReportPage />} />
            <Route path="/reports/inventory/movements" element={<StockMovementLedgerPage />} />
            <Route path="/reports/inventory/grn" element={<GRNReportPage />} />
            <Route path="/reports/inventory/adjustments" element={<StockAdjustmentReportPage />} />
            <Route path="/reports/inventory/opening-closing" element={<OpeningClosingStockReportPage />} />
          </Route>

          {/* ── Outlets (shared master — used by both Sales and Restaurant, not gated behind either app) ── */}
          <Route element={<PermissionRoute claim={Permissions.Outlets.View} />}>
            <Route path="/restaurant/outlets"      element={<OutletListPage />} />
            <Route path="/restaurant/outlets/:id"  element={<OutletDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Outlets.Create} />}>
            <Route path="/restaurant/outlets/create"     element={<OutletCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Outlets.Edit} />}>
            <Route path="/restaurant/outlets/:id/edit"   element={<OutletCreatePage />} />
          </Route>

          {/* ── Restaurant (gated behind the Restaurant app entitlement/assignment) ── */}
          <Route element={<AppRoute appCode="restaurant" />}>
          <Route element={<PermissionRoute claim={Permissions.Menu.View} />}>
            <Route path="/restaurant/menu-types"            element={<MenuTypeListPage />} />
            <Route path="/restaurant/menu-types/:id"        element={<MenuTypeDetailPage />} />
            <Route path="/restaurant/menu-categories"       element={<MenuCategoryListPage />} />
            <Route path="/restaurant/menu-categories/:id"   element={<MenuCategoryDetailPage />} />
            <Route path="/restaurant/menu-items"            element={<MenuItemListPage />} />
            <Route path="/restaurant/menu-items/:id"        element={<MenuItemDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Menu.Create} />}>
            <Route path="/restaurant/menu-types/create"          element={<MenuTypeCreatePage />} />
            <Route path="/restaurant/menu-categories/create"     element={<MenuCategoryCreatePage />} />
            <Route path="/restaurant/menu-items/create"          element={<MenuItemCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Menu.Edit} />}>
            <Route path="/restaurant/menu-types/:id/edit"        element={<MenuTypeCreatePage />} />
            <Route path="/restaurant/menu-categories/:id/edit"   element={<MenuCategoryCreatePage />} />
            <Route path="/restaurant/menu-items/:id/edit"        element={<MenuItemCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Orders.View} />}>
            <Route path="/restaurant/order"   element={<RestaurantOrderPage />} />
            <Route path="/restaurant/orders"     element={<RestaurantOrdersListPage />} />
            <Route path="/restaurant/orders/:id" element={<RestaurantOrderDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Menu.View} />}>
            <Route path="/restaurant/recipes" element={<RecipeReferencePage />} />
            <Route path="/restaurant/reports/menu-engineering" element={<MenuEngineeringReportPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Modifiers.View} />}>
            <Route path="/restaurant/modifiers" element={<MenuModifierListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Modifiers.Create} />}>
            <Route path="/restaurant/modifiers/create"   element={<MenuModifierCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Modifiers.Edit} />}>
            <Route path="/restaurant/modifiers/:id/edit" element={<MenuModifierCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.HappyHour.View} />}>
            <Route path="/restaurant/happy-hours" element={<HappyHourListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.HappyHour.Manage} />}>
            <Route path="/restaurant/happy-hours/create"   element={<HappyHourCreatePage />} />
            <Route path="/restaurant/happy-hours/:id/edit" element={<HappyHourCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.KotVoidReasons.Manage} />}>
            <Route path="/restaurant/kot-void-reasons" element={<KotVoidReasonsPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Tables.View} />}>
            <Route path="/restaurant/tables"          element={<TableListPage />} />
            <Route path="/restaurant/floor-plan"      element={<FloorPlanPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Tables.Create} />}>
            <Route path="/restaurant/tables/create"   element={<TableCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Tables.Edit} />}>
            <Route path="/restaurant/tables/:id/edit" element={<TableCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Reservations.View} />}>
            <Route path="/restaurant/reservations"          element={<ReservationListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Reservations.Create} />}>
            <Route path="/restaurant/reservations/create"   element={<ReservationCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Reservations.Edit} />}>
            <Route path="/restaurant/reservations/:id/edit" element={<ReservationCreatePage />} />
          </Route>
          </Route>

          {/* ── Car Rental (gated behind the Car Rental app entitlement/assignment) ── */}
          <Route element={<AppRoute appCode="carRental" />}>
          <Route element={<PermissionRoute claim={Permissions.RentalBookings.View} />}>
            <Route path="/car-rental/schedule" element={<CarRentalSchedulePage />} />
            <Route path="/car-rental/bookings" element={<CarRentalBookingListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Vehicles.View} />}>
            <Route path="/car-rental/vehicles" element={<VehicleListPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Vehicles.Create} />}>
            <Route path="/car-rental/vehicles/create" element={<VehicleCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Vehicles.Edit} />}>
            <Route path="/car-rental/vehicles/:id/edit" element={<VehicleCreatePage />} />
          </Route>
          </Route>

          {/* ── AutoMobile (gated behind the AutoMobile app entitlement/assignment) ── */}
          <Route element={<AppRoute appCode="automobile" />}>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileVehicles.View} />}>
            <Route path="/automobile/vehicles"     element={<AutoMobileVehicleListPage />} />
            <Route path="/automobile/vehicles/:id" element={<AutoMobileVehicleDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileVehicles.Create} />}>
            <Route path="/automobile/vehicles/create"     element={<AutoMobileVehicleCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileVehicles.Edit} />}>
            <Route path="/automobile/vehicles/:id/edit"   element={<AutoMobileVehicleCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileInvoices.View} />}>
            <Route path="/automobile/invoices"     element={<AutoMobileInvoiceListPage />} />
            <Route path="/automobile/invoices/:id" element={<AutoMobileInvoiceDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileInvoices.Create} />}>
            <Route path="/automobile/invoices/create"    element={<AutoMobileInvoiceCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AutoMobileInvoices.Edit} />}>
            <Route path="/automobile/invoices/:id/edit"  element={<AutoMobileInvoiceCreatePage />} />
          </Route>
          </Route>

          {/* ── Accounting (gated behind the Accounting app entitlement/assignment) ── */}
          <Route element={<AppRoute appCode="accounting" />}>
          <Route element={<PermissionRoute claim={Permissions.ChartOfAccounts.View} />}>
            <Route path="/accounting/chart-of-accounts"           element={<ChartOfAccountsPage />} />
            <Route path="/accounting/chart-of-accounts/:id/ledger" element={<AccountLedgerPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.GeneralLedger.View} />}>
            <Route path="/accounting/journal-entries"     element={<JournalEntriesListPage />} />
            <Route path="/accounting/journal-entries/:id" element={<JournalEntryDetailPage />} />
            <Route path="/accounting/trial-balance"       element={<TrialBalancePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.GeneralLedger.Post} />}>
            <Route path="/accounting/journal-entries/create" element={<JournalEntryCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AccountsReceivable.View} />}>
            <Route path="/accounting/accounts-receivable"                    element={<AccountsReceivablePage />} />
            <Route path="/accounting/accounts-receivable/customers/:customerId" element={<ARCustomerLedgerPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.AccountsPayable.View} />}>
            <Route path="/accounting/accounts-payable"                    element={<AccountsPayablePage />} />
            <Route path="/accounting/accounts-payable/suppliers/:supplierId" element={<APSupplierLedgerPage />} />
          </Route>
          </Route>

          {/* ── User Profile ── */}
          <Route path="/settings/profile" element={<UserProfilePage />} />

          {/* ── Notifications — personal, no permission needed ── */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* ── Data Import — gated at route level; per-entity cards gated inside the page,
               and the backend enforces per-entity Create permissions ── */}
          <Route element={<PermissionRoute claim={Permissions.Settings.View} />}>
            <Route path="/settings/data-import" element={<DataImportPage />} />
          </Route>

          {/* ── Settings ── */}
          <Route element={<PermissionRoute claim={Permissions.Settings.View} />}>
            <Route path="/settings/company" element={<CompanySettingsPage />} />
            <Route path="/settings/approval-rules" element={<ApprovalSettingsPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.Settings.Edit} />}>
            <Route path="/settings/company/edit" element={<CompanySettingsPage />} />
            <Route path="/settings/bill-printing" element={<BillPrintSettingsPage />} />
            <Route path="/settings/printing-devices" element={<PrintDevicesPage />} />
            <Route path="/settings/approval-rules/create" element={<ApprovalRuleCreatePage />} />
            <Route path="/settings/approval-rules/:id/edit" element={<ApprovalRuleEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PaymentTypes.View} />}>
            <Route path="/settings/payment-types" element={<PaymentTypeListPage />} />
            <Route path="/settings/payment-types/:id" element={<PaymentTypeDetailPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PaymentTypes.Create} />}>
            <Route path="/settings/payment-types/create" element={<PaymentTypeCreatePage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PaymentTypes.Edit} />}>
            <Route path="/settings/payment-types/:id/edit" element={<PaymentTypeEditPage />} />
          </Route>
          <Route element={<PermissionRoute claim={Permissions.PaymentTypes.Delete} />}>
            <Route path="/settings/payment-types/:id/delete" element={<PaymentTypeDeletePage />} />
          </Route>
        </Route>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
      </TillProvider>
      </CompanyProvider>
      </ModuleProvider>
      </AppAccessProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
